import React, { useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import ServiceWrapper from "../components/ServiceWrapper";
import { track } from "../utils/track";

/**
 * 기타·우쿨렐레 튜너 v2 (2026-10, 인기 튜너 앱 GuitarTuna·Fender Tune·BOSS 방식 참고).
 * - 음높이: YIN 알고리즘(기타 배음에 강함) + 줄 기준 옥타브 보정 + 중앙값 평활 + 소리가 끊겨도 잠깐 유지
 * - 화면: 큰 음 이름 · 가로 막대 · '조이세요/푸세요' 화살표 · 맞으면 초록 · 줄마다 ✓
 * - 자동(줄 자동 인식) / 수동(줄을 눌러 고정, 기준음 재생) · 조율법 선택 · 기준음 A 조절
 * 마이크 소리는 기기 안에서만 계산하고 어디에도 보내지 않는다.
 */
const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

interface IString { label: string; midi: number }
const TUNINGS: Record<string, { name: string; strings: IString[] }[]> = {
  guitar: [
    { name: "표준 (E A D G B E)", strings: [40, 45, 50, 55, 59, 64].map((m, i) => ({ label: `${6 - i}번`, midi: m })) },
    { name: "드롭 D (D A D G B E)", strings: [38, 45, 50, 55, 59, 64].map((m, i) => ({ label: `${6 - i}번`, midi: m })) },
    { name: "반음 내림 (E♭)", strings: [39, 44, 49, 54, 58, 63].map((m, i) => ({ label: `${6 - i}번`, midi: m })) },
    { name: "온음 내림 (D)", strings: [38, 43, 48, 53, 57, 62].map((m, i) => ({ label: `${6 - i}번`, midi: m })) },
    { name: "오픈 G (D G D G B D)", strings: [38, 43, 50, 55, 59, 62].map((m, i) => ({ label: `${6 - i}번`, midi: m })) },
    { name: "DADGAD", strings: [38, 45, 50, 55, 57, 62].map((m, i) => ({ label: `${6 - i}번`, midi: m })) },
  ],
  ukulele: [
    { name: "표준 (G C E A)", strings: [67, 60, 64, 69].map((m, i) => ({ label: `${4 - i}번`, midi: m })) },
    { name: "Low G (G C E A)", strings: [55, 60, 64, 69].map((m, i) => ({ label: `${4 - i}번`, midi: m })) },
    { name: "바리톤 (D G B E)", strings: [50, 55, 59, 64].map((m, i) => ({ label: `${4 - i}번`, midi: m })) },
  ],
};
const IN_TUNE = 4; // ±4센트면 맞음 (BOSS·GuitarTuna 수준)

/** YIN 음높이 추정 (de Cheveigné & Kawahara 2002). 조용하거나 확신 없으면 -1 */
const yin = (buf: Float32Array, rate: number, minF = 60, maxF = 1100): number => {
  let rms = 0;
  for (let i = 0; i < buf.length; i++) rms += buf[i] * buf[i];
  rms = Math.sqrt(rms / buf.length);
  if (rms < 0.004) return -1;
  const maxTau = Math.min(Math.floor(rate / minF), Math.floor(buf.length / 2));
  const minTau = Math.max(2, Math.floor(rate / maxF));
  const w = buf.length - maxTau;
  const d = new Float32Array(maxTau + 1);
  for (let tau = 1; tau <= maxTau; tau++) {
    let sum = 0;
    for (let i = 0; i < w; i++) {
      const delta = buf[i] - buf[i + tau];
      sum += delta * delta;
    }
    d[tau] = sum;
  }
  // 누적 평균으로 정규화
  const cmnd = new Float32Array(maxTau + 1);
  cmnd[0] = 1;
  let running = 0;
  for (let tau = 1; tau <= maxTau; tau++) {
    running += d[tau];
    cmnd[tau] = running ? (d[tau] * tau) / running : 1;
  }
  const threshold = 0.12;
  let tau = -1;
  for (let t = minTau; t <= maxTau; t++) {
    if (cmnd[t] < threshold) {
      while (t + 1 <= maxTau && cmnd[t + 1] < cmnd[t]) t++;
      tau = t;
      break;
    }
  }
  if (tau < 0) {
    // 기준 아래가 없으면 가장 낮은 곳 (확신이 너무 낮으면 버림)
    let best = minTau;
    for (let t = minTau; t <= maxTau; t++) if (cmnd[t] < cmnd[best]) best = t;
    if (cmnd[best] > 0.3) return -1;
    tau = best;
  }
  // 포물선 보간
  const x0 = tau > 1 ? cmnd[tau - 1] : cmnd[tau];
  const x2 = tau < maxTau ? cmnd[tau + 1] : cmnd[tau];
  const denom = 2 * (2 * cmnd[tau] - x2 - x0);
  const better = denom ? tau + (x2 - x0) / denom : tau;
  return rate / better;
};

const centsOff = (f: number, target: number) => 1200 * Math.log2(f / target);

const ua = navigator.userAgent;
const isAndroidApp = /chordscore\/Android/.test(ua);
const appVer = (ua.match(/csapp\/(\d+)\.(\d+)\.(\d+)/) || []).slice(1).map(Number);
const appTooOld = isAndroidApp && !(appVer.length === 3 && (appVer[0] * 10000 + appVer[1] * 100 + appVer[2]) >= 20004);
const APK = "https://chordscore.com/app/chordscore.apk";

const load = (k: string, d: string) => {
  try {
    return localStorage.getItem(k) || d;
  } catch (e) {
    return d;
  }
};
const save = (k: string, v: string) => {
  try {
    localStorage.setItem(k, v);
  } catch (e) {}
};

const Tuner: React.FC = () => {
  const [kind, setKind] = useState<"guitar" | "ukulele">(() => (load("tunerKind", "guitar") === "ukulele" ? "ukulele" : "guitar"));
  const [tuningIdx, setTuningIdx] = useState(() => Number(load("tunerTuning", "0")) || 0);
  const [a4, setA4] = useState(() => Number(load("tunerA4", "440")) || 440);
  const [manual, setManual] = useState<number | null>(null); // 수동 모드: 고정한 줄 번호
  const [on, setOn] = useState(false);
  const [err, setErr] = useState("");
  const [ask, setAsk] = useState(false);
  const [reading, setReading] = useState<{ f: number; at: number } | null>(null);
  const [done, setDone] = useState<Record<number, boolean>>({});
  const history = useRef<number[]>([]);
  const stopRef = useRef<() => void>(() => {});
  const inTuneSince = useRef<number>(0);
  const audioRef = useRef<AudioContext | null>(null);

  const tunings = TUNINGS[kind];
  const tuning = tunings[Math.min(tuningIdx, tunings.length - 1)];
  const strings = tuning.strings;
  const freqOf = (midi: number) => a4 * Math.pow(2, (midi - 69) / 12);

  const askFirst = () => {
    if (load("tunerMicOk", "") === "1") start();
    else setAsk(true);
  };

  const start = async () => {
    track("tuner:start");
    setErr("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false } as any });
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      const ctx: AudioContext = new Ctx();
      const src = ctx.createMediaStreamSource(stream);
      const hp = ctx.createBiquadFilter(); // 낮은 웅웅거림(손잡이·바람 소리) 제거
      hp.type = "highpass";
      hp.frequency.value = 55;
      const lp = ctx.createBiquadFilter(); // 높은 배음 줄이기 → 옥타브 오인식 감소
      lp.type = "lowpass";
      lp.frequency.value = 1400;
      const an = ctx.createAnalyser();
      an.fftSize = 8192; // 6번 줄(82Hz)도 여러 주기 들어오게
      src.connect(hp);
      hp.connect(lp);
      lp.connect(an);
      const buf = new Float32Array(an.fftSize);
      const half = new Float32Array(an.fftSize / 2); // 반으로 줄여(저역 통과 후라 안전) 계산량 1/4
      const timer = setInterval(() => {
        an.getFloatTimeDomainData(buf);
        for (let i = 0; i < half.length; i++) half[i] = (buf[2 * i] + buf[2 * i + 1]) / 2;
        const f = yin(half, ctx.sampleRate / 2);
        if (f > 0) {
          history.current = history.current.concat(f).slice(-7);
          const sorted = [...history.current].sort((x, y) => x - y);
          setReading({ f: sorted[Math.floor(sorted.length / 2)], at: Date.now() });
        }
      }, 70);
      stopRef.current = () => {
        clearInterval(timer);
        stream.getTracks().forEach((t) => t.stop());
        ctx.close();
      };
      setOn(true);
    } catch (e) {
      setErr(appTooOld
        ? "지금 설치된 코드스코어 앱은 마이크를 쓸 수 없는 예전 버전이에요. 아래에서 새 버전을 설치해 주세요."
        : "마이크 허용을 거절하셨거나 막혀 있어요. 다시 '튜너 시작'을 누르고 '허용'을 선택해 주세요. 그래도 안 되면 휴대폰 설정 › 애플리케이션 › 코드스코어 › 권한에서 마이크를 켜 주세요.");
    }
  };

  useEffect(() => () => {
    stopRef.current();
    audioRef.current?.close();
  }, []);

  // 화면 갱신용 (소리가 끊긴 뒤 1.5초 동안은 마지막 값을 유지)
  const [, tick] = useState(0);
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => tick((x) => x + 1), 250);
    return () => clearInterval(t);
  }, [on]);
  const live = reading && Date.now() - reading.at < 1500 ? reading.f : -1;

  // 대상 줄: 수동이면 고정, 자동이면 가장 가까운 줄. 배음(2배·절반)으로 읽힌 경우 그 줄 기준으로 옥타브 보정
  let target: IString | null = null;
  let f = live;
  if (live > 0) {
    const pick = (freq: number) => strings.reduce((best, s) => (Math.abs(centsOff(freq, freqOf(s.midi))) < Math.abs(centsOff(freq, freqOf(best.midi))) ? s : best), strings[0]);
    target = manual !== null ? strings[manual] : pick(live);
    const base = freqOf(target.midi);
    const options = [live, live / 2, live * 2].filter((x) => Math.abs(centsOff(x, base)) < 700);
    if (options.length) f = options.reduce((a, b) => (Math.abs(centsOff(a, base)) < Math.abs(centsOff(b, base)) ? a : b));
    if (manual === null) target = pick(f);
  }
  const cents = target && f > 0 ? Math.round(centsOff(f, freqOf(target.midi))) : 0;
  const ok = !!target && Math.abs(cents) <= IN_TUNE;
  const targetIdx = target ? strings.indexOf(target) : -1;

  // 0.6초 이상 맞으면 그 줄에 ✓
  useEffect(() => {
    if (!ok || targetIdx < 0) {
      inTuneSince.current = 0;
      return;
    }
    if (!inTuneSince.current) inTuneSince.current = Date.now();
    else if (Date.now() - inTuneSince.current > 600 && !done[targetIdx]) setDone((d) => ({ ...d, [targetIdx]: true }));
  });

  const playRef = (midi: number) => {
    try {
      const Ctx = (window as any).AudioContext || (window as any).webkitAudioContext;
      const ctx: AudioContext = audioRef.current || new Ctx();
      audioRef.current = ctx;
      const now = ctx.currentTime;
      const g = ctx.createGain();
      g.gain.setValueAtTime(0.0001, now);
      g.gain.exponentialRampToValueAtTime(0.35, now + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + 2.2);
      g.connect(ctx.destination);
      [1, 2, 3].forEach((h, i) => {
        const o = ctx.createOscillator();
        o.type = i === 0 ? "triangle" : "sine";
        o.frequency.value = freqOf(midi) * h;
        const hg = ctx.createGain();
        hg.gain.value = [1, 0.35, 0.15][i];
        o.connect(hg);
        hg.connect(g);
        o.start(now);
        o.stop(now + 2.3);
      });
    } catch (e) {}
  };

  const chooseKind = (k: "guitar" | "ukulele") => {
    setKind(k);
    setTuningIdx(0);
    setManual(null);
    setDone({});
    save("tunerKind", k);
    save("tunerTuning", "0");
  };

  const pos = Math.max(-50, Math.min(50, cents)); // 막대 위치 (±50센트)
  const status = !on
    ? "아래 버튼을 눌러 시작하세요"
    : !target
      ? manual !== null ? `${strings[manual].label} 줄을 튕겨 주세요` : "줄을 하나 튕겨 주세요"
      : ok ? "맞았어요" : cents < 0 ? "음이 낮아요 · 줄을 조이세요" : "음이 높아요 · 줄을 푸세요";

  return (
    <ServiceWrapper isFooterRendered={false}>
      <Page>
        <Head>
          <h1>튜너</h1>
          <Segmented role="group" aria-label="악기">
            <Seg type="button" active={kind === "guitar"} onClick={() => chooseKind("guitar")}>기타</Seg>
            <Seg type="button" active={kind === "ukulele"} onClick={() => chooseKind("ukulele")}>우쿨렐레</Seg>
          </Segmented>
        </Head>

        <Options>
          <select
            value={Math.min(tuningIdx, tunings.length - 1)}
            onChange={(e) => {
              setTuningIdx(Number(e.target.value));
              setDone({});
              setManual(null);
              save("tunerTuning", e.target.value);
            }}
            aria-label="조율법"
          >
            {tunings.map((t, i) => <option key={t.name} value={i}>{t.name}</option>)}
          </select>
          <A4 aria-label="기준음">
            <button type="button" onClick={() => { const v = Math.max(430, a4 - 1); setA4(v); save("tunerA4", String(v)); }}>−</button>
            <span>A={a4}</span>
            <button type="button" onClick={() => { const v = Math.min(450, a4 + 1); setA4(v); save("tunerA4", String(v)); }}>+</button>
          </A4>
        </Options>

        <Display state={!target ? "idle" : ok ? "ok" : "off"}>
          <BigNote>
            {target ? NOTE_NAMES[target.midi % 12] : "–"}
            {target && <small>{target.label}</small>}
          </BigNote>
          <Arrows>
            <Arrow show={!!target && !ok && cents < 0}>▲ 조이세요</Arrow>
            <Cents ok={ok}>{target ? (cents > 0 ? `+${cents}` : cents) : ""}</Cents>
            <Arrow show={!!target && !ok && cents > 0}>▼ 푸세요</Arrow>
          </Arrows>
          <Meter>
            {Array.from({ length: 21 }, (_, i) => (
              <Tick key={i} center={i === 10} major={i % 5 === 0} />
            ))}
            <Zone />
            {target && <Marker ok={ok} style={{ left: `${50 + pos}%` }} />}
          </Meter>
          <MeterLabels><span>낮음</span><span>맞음</span><span>높음</span></MeterLabels>
          <Status ok={ok}>{status}</Status>
          {target && <Hz>{f.toFixed(1)} Hz · 목표 {freqOf(target.midi).toFixed(1)} Hz</Hz>}
        </Display>

        <ModeRow>
          <span>{manual === null ? "자동: 튕긴 줄을 알아서 찾아요" : "수동: 고른 줄만 맞춰요 (누르면 기준음)"}</span>
          {manual !== null && <button type="button" onClick={() => setManual(null)}>자동으로</button>}
        </ModeRow>

        <Strings count={strings.length}>
          {strings.map((s, i) => (
            <StringBtn
              key={s.label}
              type="button"
              active={targetIdx === i}
              locked={manual === i}
              tuned={!!done[i]}
              onClick={() => {
                setManual(i);
                playRef(s.midi);
              }}
              aria-label={`${s.label} ${NOTE_NAMES[s.midi % 12]} 줄 맞추기`}
            >
              <strong>{NOTE_NAMES[s.midi % 12]}</strong>
              <span>{done[i] ? "✓" : s.label}</span>
            </StringBtn>
          ))}
        </Strings>

        {!on ? (
          <Start type="button" onClick={askFirst}>튜너 시작</Start>
        ) : (
          <Stop type="button" onClick={() => { stopRef.current(); setOn(false); setReading(null); }}>멈추기</Stop>
        )}
        {err && <Err>{err}</Err>}
        {appTooOld && <Update href={APK}>새 버전 앱 설치하기 (튜너 사용)</Update>}
        <Hint>조용한 곳에서 한 줄씩 튕기고, 소리가 울리는 동안 줄감개를 천천히 돌리세요.</Hint>

        {ask && (
          <Dim onClick={() => setAsk(false)}>
            <Sheet role="dialog" aria-label="마이크 사용" onClick={(e) => e.stopPropagation()}>
              <strong>마이크를 켤까요?</strong>
              <p>튜너는 휴대폰 마이크로 기타 소리를 듣고 음이 맞는지 알려줘요. 소리는 휴대폰 안에서만 쓰이고 어디에도 저장·전송되지 않아요.</p>
              <p className="small">다음 화면에서 휴대폰이 묻는 창에 '허용'을 눌러 주세요.</p>
              <div>
                <button type="button" onClick={() => setAsk(false)}>나중에</button>
                <button type="button" className="primary" onClick={() => { save("tunerMicOk", "1"); setAsk(false); start(); }}>마이크 켜기</button>
              </div>
            </Sheet>
          </Dim>
        )}
      </Page>
    </ServiceWrapper>
  );
};

export default Tuner;

const Page = styled.div`
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
  box-sizing: border-box;
  padding: 16px 20px 110px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  color: #f3f4f6;
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  & > h1 { margin: 0; font-size: 24px; font-weight: 700; color: #f3f4f6; }
`;

const Segmented = styled.div`
  display: flex;
  padding: 3px;
  border-radius: 10px;
  background: #16181e;
  border: 1px solid #2a2e38;
`;

const Seg = styled.button<{ active: boolean }>`
  height: 32px;
  padding: 0 14px;
  border: 0;
  border-radius: 8px;
  background: ${({ active }) => (active ? "#2b3550" : "transparent")};
  color: ${({ active }) => (active ? "#f3f4f6" : "#a3a9b5")};
  font-size: 14px;
  font-weight: ${({ active }) => (active ? 600 : 400)};
  cursor: pointer;
`;

const Options = styled.div`
  display: flex;
  gap: 8px;
  & > select {
    flex: 1;
    min-width: 0;
    height: 40px;
    padding: 0 10px;
    border-radius: 10px;
    border: 1px solid #2a2e38;
    background: #16181e;
    color: #f3f4f6;
    font-size: 14px;
  }
`;

const A4 = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  border-radius: 10px;
  border: 1px solid #2a2e38;
  background: #16181e;
  & > button { width: 34px; height: 40px; border: 0; background: none; color: #f3f4f6; font-size: 18px; cursor: pointer; }
  & > span { font-size: 13px; color: #c9cdd6; min-width: 50px; text-align: center; }
`;

const Display = styled.div<{ state: "idle" | "ok" | "off" }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 22px 18px 18px;
  border-radius: 24px;
  background: ${({ state }) => (state === "ok" ? "#0f2a1d" : "#16181e")};
  border: 1px solid ${({ state }) => (state === "ok" ? "#2f7a52" : state === "off" ? "#3a3220" : "#2a2e38")};
  transition: background 0.15s, border-color 0.15s;
`;

const BigNote = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 88px;
  font-weight: 700;
  line-height: 1;
  color: #f3f4f6;
  & > small { font-size: 16px; font-weight: 500; color: #a3a9b5; }
`;

const Arrows = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin-top: 12px;
`;

const Arrow = styled.span<{ show: boolean }>`
  flex: 1;
  font-size: 15px;
  font-weight: 700;
  color: #ffb547;
  opacity: ${({ show }) => (show ? 1 : 0)};
  &:last-of-type { text-align: right; }
`;

const Cents = styled.span<{ ok: boolean }>`
  min-width: 60px;
  text-align: center;
  font-size: 20px;
  font-weight: 700;
  color: ${({ ok }) => (ok ? "#8fe3b5" : "#f3f4f6")};
`;

const Meter = styled.div`
  position: relative;
  width: 100%;
  height: 54px;
  margin-top: 10px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Tick = styled.span<{ center: boolean; major: boolean }>`
  width: ${({ center }) => (center ? 3 : 2)}px;
  height: ${({ center, major }) => (center ? 44 : major ? 28 : 16)}px;
  border-radius: 1px;
  background: ${({ center }) => (center ? "#8fe3b5" : "#3a3f4b")};
`;

const Zone = styled.span`
  position: absolute;
  left: 46%;
  width: 8%;
  top: 2px;
  bottom: 2px;
  border-radius: 6px;
  background: rgba(143, 227, 181, 0.12);
`;

const Marker = styled.span<{ ok: boolean }>`
  position: absolute;
  top: 50%;
  width: 22px;
  height: 22px;
  margin: -11px 0 0 -11px;
  border-radius: 11px;
  background: ${({ ok }) => (ok ? "#8fe3b5" : "#ffb547")};
  box-shadow: 0 0 16px ${({ ok }) => (ok ? "rgba(143,227,181,0.8)" : "rgba(255,181,71,0.7)")};
  transition: left 0.08s linear;
`;

const MeterLabels = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  margin-top: 4px;
  font-size: 11px;
  color: #7c8392;
`;

const Status = styled.div<{ ok: boolean }>`
  margin-top: 14px;
  font-size: 17px;
  font-weight: 600;
  color: ${({ ok }) => (ok ? "#8fe3b5" : "#f3f4f6")};
  text-align: center;
`;

const Hz = styled.div`
  margin-top: 4px;
  font-size: 12px;
  color: #7c8392;
`;

const ModeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 13px;
  color: #a3a9b5;
  & > button { height: 30px; padding: 0 12px; border-radius: 8px; border: 1px solid #2a2e38; background: none; color: #8fb0ff; font-size: 13px; cursor: pointer; }
`;

const Strings = styled.div<{ count: number }>`
  display: grid;
  grid-template-columns: repeat(${({ count }) => count}, minmax(0, 1fr));
  gap: 8px;
`;

const StringBtn = styled.button<{ active: boolean; locked: boolean; tuned: boolean }>`
  height: 68px;
  border-radius: 14px;
  border: ${({ locked }) => (locked ? 2 : 1)}px solid ${({ active, tuned, locked }) => (locked ? "#5b8cff" : tuned ? "#2f7a52" : active ? "#ffb547" : "#2a2e38")};
  background: ${({ active, tuned }) => (tuned ? "#13201a" : active ? "#2a2416" : "transparent")};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  cursor: pointer;
  padding: 0;
  & > strong { font-size: 22px; color: ${({ tuned, active }) => (tuned ? "#8fe3b5" : active ? "#ffb547" : "#f3f4f6")}; }
  & > span { font-size: 11px; color: ${({ tuned }) => (tuned ? "#8fe3b5" : "#7c8392")}; }
`;

const Start = styled.button`
  height: 54px;
  border-radius: 16px;
  border: 0;
  background: #5b8cff;
  color: #0d0e12;
  font-size: 17px;
  font-weight: 700;
  cursor: pointer;
`;

const Stop = styled(Start)`
  background: transparent;
  border: 1px solid #2a2e38;
  color: #f3f4f6;
  font-weight: 500;
`;

const Err = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #ff9a9a;
  text-align: center;
  word-break: keep-all;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.6;
  color: #7c8392;
  text-align: center;
  word-break: keep-all;
`;

const Update = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  border-radius: 14px;
  background: #ffb547;
  color: #0d0e12;
  font-size: 15px;
  font-weight: 700;
  text-decoration: none;
`;

const Dim = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 1300;
  background: rgba(5, 6, 9, 0.6);
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const Sheet = styled.div`
  width: 100%;
  max-width: 520px;
  box-sizing: border-box;
  border-radius: 24px 24px 0 0;
  background: #16181e;
  border-top: 1px solid #2a2e38;
  padding: 24px 20px calc(24px + env(safe-area-inset-bottom));
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: #f3f4f6;
  word-break: keep-all;
  & > strong { font-size: 20px; }
  & > p { margin: 0; font-size: 15px; line-height: 1.6; color: #c9cdd6; }
  & > p.small { font-size: 13px; color: #7c8392; }
  & > div { display: flex; gap: 8px; margin-top: 8px; }
  & > div > button {
    flex: 1; height: 50px; border-radius: 14px; border: 1px solid #2a2e38; background: transparent;
    color: #f3f4f6; font-size: 15px; cursor: pointer;
  }
  & > div > button.primary { flex: 2; border: 0; background: #5b8cff; color: #0d0e12; font-weight: 700; }
`;
